import React, { useState, useRef, useEffect, useCallback } from "react";
import { getAnchoredMenuStyle, useCloseOnScroll } from "../Utils/anchoredMenu";
import { logisticTypes } from "../Router/constant";
import { updateBookingStatus } from "../Services/bookingService";
import ConfirmationModal from "../Components/ConfirmationModal";
import { toast } from 'react-toastify';
import { STATUS_OPTIONS, STATUS_MAPPING, RoutesPaths } from "../Router/constant";
import {
  uploadTransitSingleFile
} from "../Services/userService";
import { NavLink } from "react-router-dom";
import { FiCalendar, FiEdit2, FiEye, FiFileText, FiFilter, FiPlus, FiSearch } from "react-icons/fi";
import { MdDirectionsBoat, MdFlight, MdLocalShipping, MdTrain, MdWarehouse } from "react-icons/md";

const SERVICE_ICONS = {
  AIR_FREIGHT: MdFlight,
  SEA_FREIGHT: MdDirectionsBoat,
  RAIL_FREIGHT: MdTrain,
  TRUCKING: MdLocalShipping,
  WAREHOUSE: MdWarehouse,
};

// Ordered booking lifecycle used to draw the progress bar.
const PROGRESS_STEPS = [
  "order placed",
  "submitted for quote",
  "quote in place and sent to client for approval",
  "quote approved by client",
  "order accepted",
  "in transit",
  "delivered",
];

const getProgress = (status = "") => {
  const key = status.toLowerCase();
  if (key === "quote rejected by client") {
    return { pct: Math.round((4 / PROGRESS_STEPS.length) * 100), rejected: true };
  }
  const idx = PROGRESS_STEPS.indexOf(key);
  if (idx === -1) return { pct: 0, rejected: false };
  return { pct: Math.round(((idx + 1) / PROGRESS_STEPS.length) * 100), rejected: false };
};

export default function BookingDetails({ data = [], fetchBookings }) {
  const [activeProgress, setActiveProgress] = useState(null);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const fileInputRef = useRef(null);
  const [offset, setOffset] = useState(1);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [showFilter, setShowFilter] = useState(false);
  const limit = 5; // records per page

  const statusChoices = [...new Set(data.map((item) => item.status).filter(Boolean))];
  const query = search.trim().toLowerCase();
  const filteredData = data.filter((item) => {
    if (statusFilter && item.status !== statusFilter) return false;
    if (!query) return true;
    return [item.orderId, item.name, item.email]
      .filter(Boolean)
      .some((v) => String(v).toLowerCase().includes(query));
  });
  const count = filteredData.length; // total records
  const dropdownRef = useRef(null);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [confirmation, setConfirmation] = useState({
    show: false,
    orderId: null,
    statusValue: "",
  });
  const totalPages = Math.ceil(count / limit);
  const currentPage = offset;
  const [dropdownStyle, setDropdownStyle] = useState(null);
  const toggleDropdown = (id, event) => {
    const opening = activeDropdown !== id;
    setDropdownStyle(opening ? getAnchoredMenuStyle(event?.currentTarget) : null);
    setActiveDropdown(opening ? id : null);
  };
  const closeDropdown = useCallback(() => setActiveDropdown(null), []);
  useCloseOnScroll(Boolean(activeDropdown), closeDropdown);
  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setConfirmation((prev) => ({
      ...prev,
      attachment: file,
    }));

    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await uploadTransitSingleFile(formData);

      console.log(response, "response");

      if (response?.data?.status === true) {
        const fileName = response.data.data.fileName;

        setConfirmation((prev) => ({
          ...prev,
          uploadedFileName: fileName,
        }));
      } else {
        toast.error("File upload failed");
      }

    } catch (error) {
      console.error("File upload error:", error);
      toast.error("File upload error");
    }
  };
  const handleStatusClick = (orderId, statusValue) => {

    setConfirmation({
      show: true,
      orderId,
      statusValue,
      customStatus: statusValue.toLowerCase() === "in transit" ? "" : null,
      messageText: statusValue.toLowerCase() === "in transit" ? "" : null
    });

    setActiveDropdown(null); // close dropdown
  };



  const handleConfirm = async () => {
    const { orderId, statusValue, customStatus, messageText } = confirmation;

    // Build payload
    let payload;
    if (statusValue.toLowerCase() === "in transit") {
      payload = {
        status: "in transit",
        customStatus: customStatus || "",
        inTransitMessage: messageText || "",
        attachment: confirmation.uploadedFileName || ""
      };
    } else {
      payload = { status: statusValue };
    }

    try {
      setConfirmLoading(true);
      const response = await updateBookingStatus(orderId, payload);

      if (response.data?.status === true) {
        toast.success(response?.data?.data?.message);
        setActiveDropdown(null);
        setConfirmation({ show: false, orderId: null, statusValue: "", customStatus: "", messageText: "", attachment: "" });

        if (typeof fetchBookings === "function") {
          await fetchBookings();
        }
      } else {
        toast.error(response?.data?.data?.message || "Failed to update order status.");
      }
    } catch (error) {
      toast.error(error?.response?.data?.data?.message || "An unexpected error occurred.");
    } finally {
      setConfirmLoading(false);
    }
  };

  const handleCancel = () => {
    setConfirmation({ show: false, orderId: null, statusValue: "" });
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const generatePagination = (currentPage, totalPages) => {
    const pages = [];

    if (totalPages <= 4) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (currentPage <= 2) {
        pages.push(1, 2, '...', totalPages - 1, totalPages);
      } else if (currentPage <= 4) {
        pages.push(3, 4, '...', totalPages - 1, totalPages);
      } else if (currentPage >= totalPages - 2) {
        for (let i = totalPages - 2; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(currentPage, currentPage + 1, '...', totalPages - 1, totalPages);
      }
    }

    return pages;
  };
  const getRole = localStorage.getItem("userRole")

  const formatStatus = (status = '') => {
    const map = {
      'order placed': 'Order Placed',
      'submitted for quote': 'Submitted for Quote',
      'quote in place and sent to client for approval': 'Quote Sent to Client for Approval',
      'quote approved by client': 'Quote Approved by Client',
      'quote rejected by client': 'Quote Rejected by Client',
      'order accepted': 'Order Accepted',
      'product shipped': 'Product Shipped',
      'in transit': 'In-Transit',
      'in-transit': 'In-Transit',
      'delivered': 'Delivered',
    };

    return map[String(status).toLowerCase()] || status;
  };

  const formatStatusBadge = (status) => {
    const map = {
      pending: 'Pending',
      completed: 'Completed',
      inprogress: 'In-progress',
      'in progress': 'In-progress',

      'order placed': 'Order Placed',
      'submitted for quote': 'Submitted for Quote',
      'quote in place and sent to client for approval': 'Quote Sent to Client',
      'quote approved by client': 'Quote Approved',
      'quote rejected by client': 'Quote Rejected',

      'order accepted': 'Order Accepted',
      'product shipped': 'Product Shipped',
      'in-transit': 'In-Transit',
      'in transit': 'In-Transit',
      'delivered': 'Delivered',
    };

    return map[String(status).toLowerCase()] || status;
  };
  const formatLogisticsType = (type = '') =>
    type
      .toLowerCase()
      .replace(/_/g, ' ')
      .replace(/^./, char => char.toUpperCase());



  return (
    <div className="qd-card qd-panel qd-bookings">
      <div className="qd-panel-header qd-bookings-header">
        <div className="qd-panel-title">
          <span className="qd-panel-icon">
            <FiFileText />
          </span>
          <h6>Booking details</h6>
        </div>
        <div className="qd-bookings-tools">
          <label className="qd-search">
            <FiSearch />
            <input
              type="text"
              placeholder="Search bookings..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setOffset(1);
              }}
            />
          </label>
          <div className="position-relative">
            <button
              className={`qd-btn qd-btn-outline ${statusFilter ? "active" : ""}`}
              onClick={() => setShowFilter((v) => !v)}
            >
              <FiFilter /> Filter{statusFilter ? " (1)" : ""}
            </button>
            {showFilter && (
              <div className="qd-filter-menu">
                {["", ...statusChoices].map((status) => (
                  <button
                    key={status || "all"}
                    className={statusFilter === status ? "selected" : ""}
                    onClick={() => {
                      setStatusFilter(status);
                      setOffset(1);
                      setShowFilter(false);
                    }}
                  >
                    {status ? formatStatusBadge(status) : "All statuses"}
                  </button>
                ))}
              </div>
            )}
          </div>
          {getRole === "admin" && (
            <NavLink to={RoutesPaths.ADDPUBLICORDER} className="qd-btn qd-btn-primary">
              <FiPlus /> New Booking
            </NavLink>
          )}
        </div>
      </div>

      <div className="qd-table-wrap">
        <table className="qd-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Booking ID</th>
              <th>Customer name</th>
              <th>Service</th>
              <th>Date</th>
              <th>Order progress</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {filteredData.length === 0 ? (
              <tr>
                <td colSpan={8} className="qd-empty">No bookings found</td>
              </tr>
            ) : (
              filteredData
                .slice((offset - 1) * limit, offset * limit)
                .map((item, index) => {
                  const serviceKey = logisticTypes[item.logisticsType];
                  const ServiceIcon = SERVICE_ICONS[serviceKey] || MdLocalShipping;
                  const progressList = [...new Set(
                    (item.orderHistory || [])
                      .filter(h => h.completed)
                      .map(h => (h.status === 'product shipped' ? 'in transit' : h.status))
                  )];
                  const latestRaw = progressList[progressList.length - 1] || "";
                  const progress = getProgress(latestRaw);
                  const statusKey = item.status.toLowerCase().replace(/\s+/g, '');

                  return (
                    <tr key={item._id}>
                      <td className="qd-muted">{(offset - 1) * limit + index + 1}</td>
                      <td className="qd-mono">#{item.orderId}</td>
                      <td>
                        <span className="qd-customer">
                          <span className="qd-avatar">
                            {(item.name || "?").trim().charAt(0).toUpperCase()}
                          </span>
                          <strong>{item.name}</strong>
                        </span>
                      </td>
                      <td>
                        <span className="qd-service">
                          <ServiceIcon />
                          {formatLogisticsType(serviceKey) || "N/A"}
                        </span>
                      </td>
                      <td>
                        <span className="qd-date">
                          <FiCalendar />
                          {new Date(item.createdAt).toLocaleDateString("en-GB")}
                        </span>
                      </td>
                      <td>
                        {progressList.length === 0 ? (
                          "-"
                        ) : (
                          <div className="qd-progress">
                            <div className="qd-progress-label">
                              {formatStatus(latestRaw)}
                              {progressList.length > 1 && (
                                <span
                                  className="qd-more"
                                  onClick={() => setActiveProgress(progressList.map(formatStatus))}
                                >
                                  +{progressList.length - 1} more
                                </span>
                              )}
                            </div>
                            <div className="qd-progress-row">
                              <div className="qd-progress-track">
                                <div
                                  className={`qd-progress-fill ${progress.rejected ? "rejected" : ""}`}
                                  style={{ width: `${progress.pct}%` }}
                                />
                              </div>
                              <span>{progress.pct}%</span>
                            </div>
                          </div>
                        )}
                      </td>
                      <td>
                        <span className={`qd-status qd-status-${statusKey}`}>
                          {formatStatusBadge(item.status)}
                        </span>
                      </td>
                      <td>
                        <div
                          className="qd-actions position-relative"
                          ref={activeDropdown === item._id ? dropdownRef : null}
                        >
                          <NavLink
                            to={`${RoutesPaths.BOOKINGMANAGEMENTORDERHISTORY}/${item?._id}`}
                            className="qd-btn qd-btn-outline qd-btn-sm"
                          >
                            <FiEye /> View
                          </NavLink>
                          <button
                            className={`qd-btn qd-btn-outline qd-btn-sm ${getRole === 'partner' && !item?.isAccepted
                              ? 'disbaledEditBooking'
                              : ''
                              }`}
                            onClick={(event) => toggleDropdown(item._id, event)}
                          >
                            <FiEdit2 /> Edit
                          </button>

                          {activeDropdown === item._id && (
                            <div className="actionDropdownOuter qd-anchored-menu" style={dropdownStyle || undefined}>
                              <ul className="actionDropdownList">
                                {STATUS_OPTIONS.map((status) => {
                                  const completedHistory = item.orderHistory?.filter(h => h.completed) || [];
                                  const latestCompleted = completedHistory[completedHistory.length - 1]?.status.toLowerCase() || "";

                                  return (
                                    <li
                                      key={status}
                                      className={`actionDropdownItem ${latestCompleted === status.toLowerCase() ? "selected" : ""}`}
                                      onClick={() => handleStatusClick(item._id, status)}
                                    >
                                      {status.toLowerCase() === "in transit"
                                        ? "Other"
                                        : status.charAt(0).toUpperCase() + status.slice(1)}
                                    </li>
                                  );
                                })}
                              </ul>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
            )}
          </tbody>
        </table>
      </div>

      {activeProgress && (
        <div className="progressModalOverlay" onClick={() => setActiveProgress(null)}>
          <div
            className="progressModal"
            onClick={(e) => e.stopPropagation()}
          >
            <h4>Order Progress</h4>

            <ul>
              {activeProgress.map((step, i) => (
                <li key={i}>{step}</li>
              ))}
            </ul>

            <div className="modalFooter">
              <button
                className="btn btn-cancel"
                onClick={() => setActiveProgress(null)}
              >
                Close
              </button>
            </div>          </div>
        </div>
      )}

      {count > 0 && totalPages > 1 && (
        <div className="qd-pagination">
          <span className="qd-muted">
            Showing {(offset - 1) * limit + 1}-{Math.min(offset * limit, count)} of {count}
          </span>
          <div className="qd-pages">
            <button disabled={offset <= 1} onClick={() => setOffset(offset - 1)} aria-label="Previous">
              <img src="/assets/img/arrow-left.svg" alt="" />
            </button>
            {generatePagination(currentPage, totalPages).map((page, idx) =>
              page === '...' ? (
                <span key={idx} className="qd-ellipsis">...</span>
              ) : (
                <button
                  key={idx}
                  className={currentPage === page ? "active" : ""}
                  onClick={() => setOffset(page)}
                >
                  {page}
                </button>
              )
            )}
            <button disabled={offset >= totalPages} onClick={() => setOffset(offset + 1)} aria-label="Next">
              <img src="/assets/img/arrow-right.svg" alt="" />
            </button>
          </div>
        </div>
      )}


      <ConfirmationModal
        show={confirmation.show}
        title={
          confirmation.statusValue.toLowerCase() === "in transit"
            ? "Add custom tracking status"
            : "Update status"
        }
        message={
          confirmation.statusValue.toLowerCase() === "in transit" ? (
            <div>
              <p>Please enter a custom tracking status for the user to see:</p>
              <input
                type="text"
                placeholder="Custom tracking status"
                value={confirmation.customStatus || ""}
                onChange={(e) =>
                  setConfirmation({ ...confirmation, customStatus: e.target.value })
                }
                style={{ width: "100%", marginBottom: "8px", padding: "6px" }}
              />
              <textarea
                placeholder="Enter here message"
                value={confirmation.messageText || ""}
                onChange={(e) =>
                  setConfirmation({ ...confirmation, messageText: e.target.value })
                }
                style={{ width: "100%", padding: "6px" }}
              />
              <div
                style={{
                  border: "2px dashed #d1d5db",
                  borderRadius: "6px",
                  padding: "12px",
                  textAlign: "center",
                  background: "#fafafa",
                }}
              >
                <input
                  type="file"
                  id="attachmentUpload"
                  name="file"
                  ref={fileInputRef}
                  accept="image/*,application/pdf"
                  style={{ display: "none" }}
                  onChange={handleFileChange}
                />
                <label
                  htmlFor="attachmentUpload"
                  style={{
                    cursor: "pointer",
                    color: "#ff4d2d",
                    fontWeight: "500",
                  }}
                >
                  Click to upload file
                </label>

                <p style={{ fontSize: "12px", color: "#888", marginTop: "4px" }}>
                  PNG, JPG, PDF, DOC (Max 5MB)
                </p>
                {console.log(confirmation, 'confirmation')}
                {confirmation?.attachment && (
                  <div
                    style={{
                      marginTop: "8px",
                      padding: "6px 10px",
                      background: "#f1f5f9",
                      borderRadius: "4px",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <span style={{ fontSize: "13px" }}>
                      📎 {confirmation.attachment?.name}
                    </span>

                    <button
                      type="button"
                      onClick={() => {
                        setConfirmation((prev) => ({
                          ...prev,
                          attachment: null,
                        }));


                      }}
                      style={{
                        border: "none",
                        background: "transparent",
                        color: "red",
                        cursor: "pointer",
                      }}
                    >
                      Remove
                    </button>
                  </div>
                )}

              </div>
            </div>
          ) : (
            `Are you sure you want to update the status to "${confirmation.statusValue.replace(
              /\b\w/g,
              (char) => char.toUpperCase()
            )}"?`
          )
        }
        onConfirm={handleConfirm}
        onCancel={() =>
          setConfirmation({
            show: false,
            orderId: null,
            statusValue: "",
            customStatus: "",
            messageText: "",
            attachment: ""
          })
        }
        confirmText={confirmLoading ? "Confirming..." : (
          confirmation.statusValue.toLowerCase() === "in transit"
            ? "Save status"
            : "Confirm"
        )}
        confirmDisabled={confirmLoading}
        cancelText="Cancel"
      />


    </div>
  );
}
